import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";

import Stack from "@mui/material/Stack";
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import StraightenIcon from "@mui/icons-material/Straighten";
import { useNavigate } from "react-router-dom";

import { SIZE_CHARTS } from "app/routes/constant";
import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import {
  createSize,
  deleteSize,
  getSizeList,
  updateSize,
} from "app/redux/slice/sizeSlice";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import AddSize from "./add-size";

const Size = () => {
  const { isOpen, onClose, onOpen, stageAction, actionProps } = useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();
  const navigate = useNavigate();

  const sizeData = useSelector((state) => state?.size);
  const [searchText, setSearchText] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [isInitialized, setIsInitialized] = useState(false);

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "size" });
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
    sizeChartPermission: false,
  });
  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
        sizeChartPermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
        sizeChartPermission: false,
      };
      const rolePermission = permissionData;
      rolePermission?.length &&
        rolePermission.forEach(function (num) {
          if (num?.name === "create-size") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-size") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-size") {
            permissionValue.updatePermission = true;
          }
        });
      // Checked against the whole list, not the filtered one above: size chart
      // permissions sit under their own module name, so the "Size" filter
      // would never see them.
      permissionValue.sizeChartPermission = Boolean(
        permissionData?.some((item) => item?.name === "size-chart-list")
      );
      setPermission(permissionValue);
    }
  };
  useEffect(() => {
    getPermission();
    
    const savedSearchText = localStorage.getItem("size-search") || "";
    setSearchText(savedSearchText);
    setIsInitialized(true);
  }, []);

  const columns = [
    { id: "name", label: "Name", sort: true },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      align: "right",
      render: (row) => (
        <>
          {permission.updatePermission && (
            <IconButton
              onClick={() => {
                stageAction(row);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {permission.deletePermission && (
            <IconButton
              onClick={() => {
                deleteStageAction(row);
              }}
            >
              <DeleteIcon color={"error"} />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const handleSubmit = async (data) => {
    const newTaxData = {
      name: data.name?.trim(),
    };
    const response = await dispatch(
      actionProps?._id
        ? updateSize({ id: actionProps._id, data: newTaxData })
        : createSize(newTaxData)
    ).unwrap();
    if (response.status === 200) {
      enqueueSnackbar(response.data?.responseMessage, {
        variant: "success",
      });
      getData();
      onClose();
    } else {
      enqueueSnackbar(response.data?.responseMessage, {
        variant: "error",
      });
    }
  };

  const DeleteSize = () => {
    dispatch(deleteSize(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  const getData = () => {
    localStorage.setItem("size-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getSizeList(value));
  };

  useEffect(() => {
    if (isInitialized) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy, isInitialized]);

  useEffect(() => {
    if (isInitialized && searchText !== "") {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  return (
    <>
      {sizeData.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={10}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Sizes List
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: { xs: 1, md: 0 } }}
                >
                  {permission.sizeChartPermission && (
                    <Button
                      variant="outlined"
                      startIcon={<StraightenIcon />}
                      onClick={() => navigate(SIZE_CHARTS)}
                    >
                      Size Charts
                    </Button>
                  )}
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={() => {
                        onOpen();
                      }}
                      type="submit"
                      variant="contained"
                    >
                      Add Size
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
              <Table
                columns={columns}
                rows={sizeData.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={sizeData.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
          <DialogDefault
            open={isOpen}
            onClose={onClose}
            content={
              <AddSize
                value={actionProps}
                onClose={onClose}
                handleSubmit={handleSubmit}
              />
            }
            title={actionProps?._id ? "Update Size" : "Add Size"}
            fullWidth={true}
            maxWidth={"sm"}
            actions={<></>}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete the size?"
            onConfirm={DeleteSize}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default Size;
